// Client HTTP vers l'API PHP.
//
// Tout passe par ici : une seule place pour l'URL de base, le token,
// l'en-tete ngrok et la gestion d'erreur.
//
// Ce module n'est pas un composant : il lit la langue via getDict() plutot
// que par un hook. Les messages d'erreur RENVOYES PAR LE SERVEUR passent
// tels quels — c'est au backend de les traduire, pas a nous de les deviner.
import { getDict } from './i18n'

// L'URL vient de .env (REACT_APP_API_URL). Le repli local sert si le
// fichier manque — Apache de ce poste ecoute sur 1234, pas 80.
//
// EXPORTEE, et c'est important : c'est la SEULE definition de l'adresse du
// backend dans tout le front. Quand ngrok redemarre, une seule ligne du .env
// change et tout suit. Ne JAMAIS recopier ce `process.env.REACT_APP_API_URL`
// ailleurs — un second exemplaire, c'est un endroit de plus a oublier le jour
// du deploiement, et la panne est silencieuse.
// Le repli est l'adresse de PRODUCTION, pas localhost : un build livre sans
// .env tomberait sinon sur http://localhost:1234, que le navigateur du
// client ne peut evidemment pas joindre — panne totale et muette. Mieux
// vaut un repli qui marche en ligne et qu'on surcharge en local.
export const BASE = process.env.REACT_APP_API_URL || 'https://api.selfcntrl.com'

const TOKEN_KEY = 'ctrl_token'

// Le token vit dans localStorage : il survit au rechargement de la page.
// A savoir : un script injecte dans la page pourrait le lire (XSS). C'est
// le compromis habituel pour ce type d'app ; le durcir passerait par un
// cookie httpOnly, ce qui demande de toucher au backend.
export const getToken = () => localStorage.getItem(TOKEN_KEY)
export const setToken = t => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY)
export const clearToken = () => localStorage.removeItem(TOKEN_KEY)

/** Erreur enrichie du code HTTP, pour distinguer 401 / 402 / 409. */
export class ApiError extends Error {
  constructor (message, status, data) {
    super(message)
    this.status = status
    this.data = data
  }
}

async function request (path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'ngrok-skip-browser-warning': 'true' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = getToken()
  if (auth && token) headers.Authorization = `Bearer ${token}`

  let res
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined
    })
  } catch (e) {
    // Reseau coupe, Apache eteint, mauvaise URL...
    throw new ApiError(getDict().errors.network, 0, null)
  }

  const text = await res.text()
  let data = null
  try { data = text ? JSON.parse(text) : null } catch { /* reponse non JSON */ }

  if (!res.ok) {
    // 401 = token mort : on nettoie pour ne pas boucler.
    if (res.status === 401) clearToken()
    throw new ApiError(data?.error || getDict().errors.http(res.status), res.status, data)
  }

  return data
}

export const api = {
  get: (p, o) => request(p, { ...o, method: 'GET' }),
  post: (p, body, o) => request(p, { ...o, method: 'POST', body })
}

// --- Points d'entree nommes ------------------------------------------------
export const authApi = {
  register: payload => api.post('/auth/register.php', payload, { auth: false }),
  login: (email, password) => api.post('/auth/login.php', { email, password }, { auth: false }),
  claim: (email, password) => api.post('/auth/claim.php', { email, password }, { auth: false }),
  me: () => api.get('/auth/me.php'),
  logout: () => api.post('/auth/logout.php', {}),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password.php', { current_password, new_password }),
  // Fuseau IANA du compte (cale les couvre-feux Cloudflare sur l'heure locale).
  setTimezone: timezone => api.post('/auth/timezone.php', { timezone }),
  // Prénom / pseudo choisi dans le tunnel /welcome (et « Mon compte »).
  setName: name => api.post('/auth/name.php', { name })
}

export const childrenApi = {
  list: () => api.get('/children/list.php'),
  create: payload => api.post('/children/create.php', payload),
  update: payload => api.post('/children/update.php', payload),
  remove: id => api.post('/children/delete.php', { id }),

  // Retrait différé : la demande ouvre un délai de réflexion, et le mot de
  // passe qui désinstalle la supervision n'arrive qu'à son terme.
  removalStatus: childId => api.get(`/children/removal-status.php?child_id=${childId}`),
  removalRequest: payload => api.post('/children/removal-request.php', payload),
  removalCancel: child_id => api.post('/children/removal-cancel.php', { child_id }),

  // Rythme de sevrage. Le serveur refuse toute semaine plus permissive que
  // le plancher déjà atteint : le cliquet vit là-bas, pas ici.
  weaning: (child_id, days) => api.post('/children/weaning.php', { child_id, days }),
  weaningCancel: child_id =>
    api.post('/children/weaning.php', { child_id, cancel_pending: true }),

  // « Bloquer plus » : familles de catégories + domaines perso, en plus du
  // filtrage adulte. patch = { block_groups?, add_domain?, remove_domain? }.
  blocklist: (child_id, patch) => api.post('/children/blocklist.php', { child_id, ...patch })
}

export const devicesApi = {
  list: () => api.get('/devices/list.php'),
  create: payload => api.post('/devices/create.php', payload),
  remove: id => api.post('/devices/delete.php', { id }),
  // État « en direct » : actif/inactif + dernière fois vu, par appareil.
  status: () => api.get('/devices/status.php')
}

export const installApi = {
  createToken: child_id => api.post('/install/create-token.php', { child_id })
}

export const schedulesApi = {
  list: childId => api.get(`/schedules/list.php?child_id=${childId}`),
  create: payload => api.post('/schedules/create.php', payload),
  update: payload => api.post('/schedules/update.php', payload),
  remove: id => api.post('/schedules/delete.php', { id })
}

export const pairApi = {
  // Code d'association a taper dans l'app de supervision sur le PC/Mac.
  create: child_id => api.post('/pair/create.php', { child_id })
}

export const subscriptionApi = {
  status: () => api.get('/subscription/status.php'),
  // Ne coupe rien : envoie une alerte pour une resiliation faite a la main.
  cancelRequest: reason => api.post('/subscription/cancel-request.php', { reason })
}

export const statsApi = {
  overview: () => api.get('/stats/overview.php'),
  // Tentations bloquees (Cloudflare Gateway). Appel un peu plus lourd cote
  // serveur : on le met en cache 24 h dans le navigateur, cf. blockedCached().
  blocked: () => api.get('/stats/blocked.php')
}

// Cache navigateur 24 h pour les tentations bloquees : une requete Cloudflare
// par jour et par appareil suffit. La cle est propre a l'utilisateur pour ne
// pas montrer les chiffres d'un compte a un autre sur le meme navigateur.
export function blockedCached (userId) {
  // v2 : le champ by_hour est arrivé ; l'ancienne clé expire d'elle-même.
  const key = `sc_blocked2_${userId}`
  const TTL = 24 * 60 * 60 * 1000
  try {
    const raw = localStorage.getItem(key)
    if (raw) {
      const { ts, data } = JSON.parse(raw)
      if (Date.now() - ts < TTL) return Promise.resolve(data)
    }
  } catch { /* cache illisible : on refait l'appel */ }
  return statsApi.blocked().then(data => {
    try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })) } catch {}
    return data
  })
}

export const goalsApi = {
  list: () => api.get('/goals/list.php'),
  create: payload => api.post('/goals/create.php', payload),
  // { id, delta } pour les boutons + / −, ou { id, done, title, target, unit }
  // Renvoie { goal, credited } : `credited` = CNTRL gagnés à l'instant.
  update: payload => api.post('/goals/update.php', payload),
  remove: id => api.post('/goals/delete.php', { id }),
  reset: () => api.post('/goals/reset.php', {}),
  // Les cycles déjà refermés (« la semaine dernière ») + les totaux.
  history: () => api.get('/goals/history.php')
}

// Les CNTRL : le solde, l'historique du grand livre, l'état du compteur, et la
// commande de carte cadeau. Le débit est réel et immédiat ; l'envoi de la
// carte est manuel (une notification Telegram nous prévient) — voir redeem.php.
export const cntrlApi = {
  balance: () => api.get('/cntrl/balance.php'),
  redeem: (brand, cards = 1) => api.post('/cntrl/redeem.php', { brand, cards }),
  // Opt-in WhatsApp : crédite 100 CNTRL une seule fois par compte (idempotent
  // côté serveur). Renvoie { credited, already, balance }.
  claimWhatsapp: () => api.post('/cntrl/whatsapp-optin.php', {}),
  // Opt-in Discord : idem, 100 CNTRL une seule fois (ref 'p:discord-join').
  claimDiscord: () => api.post('/cntrl/discord-optin.php', {})
}

export const streakApi = {
  status: () => api.get('/streak/status.php'),
  // Déclare une rechute : archive le record et repart de zéro.
  reset: () => api.post('/streak/reset.php', {})
}

export const leaderboardApi = {
  list: (limit = 25) => api.get(`/leaderboard/list.php?limit=${limit}`),
  optin: (share, alias) => api.post('/leaderboard/optin.php', { share, alias })
}

// Le seul endpoint PUBLIC du lot : le formulaire de contact est dans
// l'en-tete de la page d'accueil, quelqu'un sans compte doit pouvoir ecrire.
// Le jeton part quand meme s'il existe — cote serveur il sert seulement a
// rattacher le message a un compte, jamais a autoriser l'envoi.
export const contactApi = {
  send: (email, message) => api.post('/contact/send.php', { email, message }),
  // « Previens-moi quand c'est pret ». Le sujet est valide cote serveur
  // contre une liste blanche : il finit dans un nom de fichier.
  waitlist: (email, topic) => api.post('/contact/waitlist.php', { email, topic })
}

export const cloudflareApi = {
  status: () => api.get('/cloudflare/status.php'),
  provision: () => api.post('/cloudflare/provision.php', {})
}

export const stripeApi = {
  // Renvoie l'URL de la page Stripe. On ne construit jamais de formulaire
  // de carte nous-memes : c'est Stripe qui heberge la page de paiement.
  checkout: plan => api.post('/stripe/checkout.php', { plan }),
  portal: () => api.post('/stripe/portal.php', {})
}

/** Demande la session puis envoie le navigateur sur Stripe. */
export async function goToCheckout (plan) {
  const { url } = await stripeApi.checkout(plan)
  window.location.href = url
}
